import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { offsetShadow, TYPE } from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";

export type BrBtnSize = "xs" | "sm" | "md" | "lg";
export type BrBtnVariant = "primary" | "accent" | "success" | "danger" | "surface" | "ghost";

interface Props {
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  size?: BrBtnSize;
  variant?: BrBtnVariant;
  withShadow?: boolean;
  full?: boolean;
  style?: CSSProperties;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  title?: string;
  ariaLabel?: string;
}

const SIZES: Record<BrBtnSize, CSSProperties> = {
  xs: { padding: "5px 9px", fontSize: 11, gap: 4 },
  sm: { padding: "8px 14px", fontSize: 12.5, gap: 6 },
  md: { padding: "11px 18px", fontSize: 13.5, gap: 8 },
  lg: { padding: "15px 26px", fontSize: 15, gap: 10 },
};

export function BrBtn({
  children,
  onClick,
  size = "md",
  variant = "primary",
  withShadow = true,
  full = false,
  style = {},
  disabled = false,
  type = "button",
  title,
  ariaLabel,
}: Props) {
  const p = useTheme();
  const variants: Record<BrBtnVariant, CSSProperties> = {
    primary: { background: p.fg, color: p.bg },
    accent: { background: p.accent, color: p.accentFg },
    success: { background: p.success, color: p.successFg },
    danger: { background: p.danger, color: p.dangerFg },
    surface: { background: p.card, color: p.fg },
    ghost: { background: "transparent", color: p.fg, borderColor: p.border },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: `2px solid ${p.border}`,
        fontFamily: TYPE.body,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        boxShadow: withShadow ? offsetShadow(p, 4, 4) : "none",
        transition: "transform .08s, box-shadow .08s, background .25s, color .25s",
        ...SIZES[size],
        ...variants[variant],
        ...(full ? { width: "100%" } : {}),
        ...style,
      }}
      onMouseDown={(e) => {
        if (disabled || !withShadow) return;
        e.currentTarget.style.transform = "translate(2px, 2px)";
        e.currentTarget.style.boxShadow = offsetShadow(p, 2, 2);
      }}
      onMouseUp={(e) => {
        if (disabled || !withShadow) return;
        e.currentTarget.style.transform = "translate(0, 0)";
        e.currentTarget.style.boxShadow = offsetShadow(p, 4, 4);
      }}
      onMouseLeave={(e) => {
        if (disabled || !withShadow) return;
        e.currentTarget.style.transform = "translate(0, 0)";
        e.currentTarget.style.boxShadow = offsetShadow(p, 4, 4);
      }}
    >
      {children}
    </button>
  );
}
